<template>
  <div class="acc_list_main_filter">
    <PartsTempPreloader v-if="loading"/>
    <div class="price-slider mb-3" v-if="filterOptions.prices.from < filterOptions.prices.to">
      <p class="h4">{{ $t('price-per-night') }}</p>

      <div class="slider-container pb-5">
        <PriceRangeSlider :max_value="filterOptions.prices.to"
                          :min_value="filterOptions.prices.from"
                          :step="1"
                          :default="filterData.price"
                          :open="open"
                          :activeLocale="activeLocale"
                          :currency="currency"
                          @change="onChangePrice"/>
      </div>

    </div>
    <div class="order-by mb-5">
      <p class="h4">{{ $t('order-by') }}</p>
      <div class="row">

        <div class="col-6" v-for="type in ['recommended', 'rating', 'price_from', 'price_to']">

          <label class="custom-label">
            <input v-model="filterData.order_by" :value="type" name="smoking_policy"
                   type="radio">
            <span class="title">{{ $t('order-by-' + type) }}</span>
            <span class="checkmark checkmark-radio"></span>
          </label>
        </div>
      </div>
    </div>
    <div class="mb-5" v-if="filterOptions.categories && filterOptions.categories.length > 0">
      <p class="h4">{{ $t('accommodation-category') }}</p>
      <div class="row">
        <div class="col-6"
             v-for="category in sortItemsByCount(filterOptions.categories)">

          <label class="custom-label">
            <input v-model="filterData.type" :value="category.code" :name="category.code"
                   type="checkbox" @change="onChangeFilter">
            <span class="title">{{ $t('acc-type-' + category.code) }} ({{ category.count }})</span>
            <span class="checkmark"></span>
          </label>
        </div>
      </div>
    </div>
    <div class="favoriteProperties mb-5" v-if="favoriteProperties.length > 0">
      <p class="h4">
        <TyIcon icon="ty-heart" color="red" width="1.2rem" height="1.2rem"></TyIcon>
        {{ $t('favorite-equipments') }}</p>
      <div class="row">
        <div class="col-12 col-md-6" v-for="equipment in sortItemsByCount(favoriteProperties)">

          <label class="custom-label">
            <input v-model="filterData.properties" :value="`${equipment.group}__${equipment.code}`"
                   :name="equipment.code"
                   type="checkbox" @change="onChangeFilter">
            <span class="title">{{
                $t(equipment.group + '-' + equipment.code, 'equipment-' + equipment.code)
              }} ({{ equipment.count }})</span>
            <span class="checkmark"></span>
          </label>
        </div>
      </div>
    </div>
    <!-- <template v-if="favoriteProperties.length > 0">
          <hr>
          <p class="mb-0 h4">
            {{$t('other-equipments')}}
          </p>
          <hr>
        </template>-->

    <div v-for="eqGroup in filterOptions.equipments" class="mb-5">
      <p class="h4" type="button" data-bs-toggle="collapse" :data-bs-target="`#collapse_group_${eqGroup.code}`" >{{ $t(eqGroup.code, eqGroup.code + '-title') }}</p>

      <div class="row collapse" :id="`collapse_group_${eqGroup.code}`"
           :class="filterData.properties.some(p => p.substring(0, p.indexOf('__')) === eqGroup.code) ? 'show' : ''">
        <div class="col-6 col-md-6" v-for="equipment in sortItemsByCount(eqGroup.items)">

          <label class="custom-label">
            <input v-model="filterData.properties" :value="`${eqGroup.code}__${equipment.code}`" :name="equipment.code"
                   type="checkbox" @change="onChangeFilter">
            <span class="title">{{
                $t(eqGroup.code + '-' + equipment.code, 'equipment-' + equipment.code)
              }} ({{ equipment.count }})</span>
            <span class="checkmark"></span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {computed, onMounted, PropType, ref, watch} from "vue";
import PriceRangeSlider from "~/components/web/parts/price_range_slider.vue"
import {clearObserverObj} from "../../../../../lib/functions";
import {useNuxtApp} from "#app";
import favoriteJsonData from "~/lib/data/filter_favorite_equipments.json";

const {$tyrionApi} = useNuxtApp();

const props = defineProps({
  defaultFilterParams: Object,
  open: Boolean,
  currency: String,
  activeLocale: Object as PropType<{ code: string }>,
});

const filterData = ref({order_by: 'rating', price: {from: 0, to: 0}, properties: [], type: []});
const filterOptions = ref({categories: [], equipments: [], prices: {from: 0, to: 0}});
const loading = ref(true);
const totalCount = ref(0);
const changeFilter = ref<null | number>(null);

watch(() => props.open, (newVal) => {
  if (newVal === true) {
    loadDefaultFilterData();
    loadFilterOptions();
  }
});

const favoriteProperties = computed(() => {
  const favorites = [];
  const localeFavorites = favoriteJsonData[props.activeLocale.code] || favoriteJsonData['default'];

  if (localeFavorites) {
    filterOptions.value.equipments.forEach(group => {
      group.items.forEach(eq => {
        const code = `${group.code}__${eq.code}`;
        eq.group = group.code;

        if (localeFavorites.includes(code)) {
          favorites.push(eq);
        }
      });
    });
  }

  return favorites;
});

const loadDefaultFilterData = () => {
  Object.keys(props.defaultFilterParams).forEach((key) => {
    if (key === 'price') {
      props.defaultFilterParams[key].from = Number(props.defaultFilterParams[key].from);
      props.defaultFilterParams[key].to = Number(props.defaultFilterParams[key].to);
    }
    filterData.value[key] = props.defaultFilterParams[key];
  });
}

const sortItemsByCount = (items) => {
  return (JSON.parse(JSON.stringify(items))).sort((a, b) => a.count > b.count ? -1 : 1);
}

const getFilterData = () => {
  return clearObserverObj(filterData.value);
}

const loadFilterOptions = () => {
  if (changeFilter.value !== null) clearTimeout(changeFilter.value);
  changeFilter.value = setTimeout(() => {
    loading.value = true;
    $tyrionApi.postTyrionUrl('/api3/accommodation/filter-options', {
      body: {
        currency: props.currency,
        filter: filterData.value,
      }
    }).then(res => {
      if (res.status === 'success') {
        filterOptions.value = res.data.options;
        totalCount.value = res.data.count;
      }
    }).finally(() => {
      loading.value = false;
    });
  }, 800);
}

const onChangeFilter = () => {
  loadFilterOptions();
}

const onChangePrice = (c) => {
  filterData.value.price = {from: Number(c.from), to: Number(c.to)};
  onChangeFilter();
}

const obtainTotalItemsCount = computed(() => {
  return totalCount.value;
})

defineExpose({
  getFilterData, obtainTotalItemsCount
})
</script>
